import { useEffect } from "react";

import { ErrorState, Loading } from "../../components/WorkspacePrimitives";
import { IconButton, RestoreIcon, SidePeek } from "../../components/ui";

import {
  useXLMRevisions,
  type XLMRevisionKind,
  type XLMRevisionSummary,
} from "../api/e2e";
import { relativeTime } from "../lib/format";

function revisionKindLabel(kind: XLMRevisionKind): string {
  if (kind === "published") return "Published";
  if (kind === "annotation") return "Note";
  if (kind === "restore") return "Restore";
  return "Edit";
}

function revisionTimestampTitle(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return date.toLocaleString();
}

function revisionAuthorLabel(value: string | null): string {
  if (!value || value.startsWith("system:")) return "System";
  if (value.startsWith("api-token:")) return "API token";
  return value;
}

function lineItemLabel(identifier: string): string {
  const label = identifier.replaceAll("_", " ");
  return label.charAt(0).toLocaleUpperCase() + label.slice(1);
}

function revisionPeriodLabel(
  collection: string,
  year: string,
  index: string | undefined,
): string {
  const fiscalYear = `FY${year.slice(-2)}`;
  if (collection.includes("quarter") && index && index !== "0") {
    return `Q${index} ${fiscalYear}`;
  }
  if (collection.includes("half") && index && index !== "0") {
    return `H${index} ${fiscalYear}`;
  }
  if (collection.includes("year")) return fiscalYear;
  return index && index !== "0" ? `${year} · period ${index}` : year;
}

function changeLabel(change: string): string {
  const [kind, identifier, collection, year, index] = change.split(":");
  if (kind === "restore" && identifier) {
    return `Restored from revision ${identifier}`;
  }
  if (kind === "cell" && identifier && collection && year) {
    return `${lineItemLabel(identifier)} · ${revisionPeriodLabel(collection, year, index)}`;
  }
  return change.replaceAll("_", " ");
}

function revisionEditLabel(revision: XLMRevisionSummary): string {
  if (revision.changes.length > 0) {
    return revision.changes.map(changeLabel).join("; ");
  }
  if (revision.restored_from_revision !== null) {
    return `Restored from revision ${revision.restored_from_revision}`;
  }
  return "Published snapshot";
}

export default function AgentXLMRevisionHistory({
  companyId,
  versionId,
  onClose,
  onRequestRestore,
}: {
  companyId: string;
  versionId: string;
  onClose: () => void;
  onRequestRestore: (revision: XLMRevisionSummary) => void;
}) {
  const revisions = useXLMRevisions(companyId, versionId);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <SidePeek
      id="xlm-revision-history-sidepeek"
      className="xlm-revision-history"
      ariaLabel="Revision history side peek"
      eyebrow={
        revisions.data
          ? `${revisions.data.revisions.length} ${
              revisions.data.revisions.length === 1 ? "revision" : "revisions"
            }`
          : undefined
      }
      title="Revision history"
      closeLabel="Close revision history"
      onClose={onClose}
    >
      <div className="xlm-revision-history__body">
        {revisions.isPending && !revisions.data ? (
          <Loading label="Loading revision history…" />
        ) : revisions.error && !revisions.data ? (
          <ErrorState
            error={revisions.error}
            onRetry={() => revisions.refetch()}
          />
        ) : revisions.data ? (
          <ol
            className="xlm-revision-history__timeline"
            aria-label="Revisions, newest first"
          >
            {revisions.data.revisions.map((revision) => {
              const current =
                revision.revision === revisions.data?.head_revision;
              const edit = revisionEditLabel(revision);
              const author = revisionAuthorLabel(revision.created_by);
              return (
                <li
                  key={revision.revision}
                  className={current ? "is-current" : undefined}
                  aria-current={current ? "true" : undefined}
                >
                  <article className="xlm-revision-history__entry">
                    <header className="xlm-revision-history__entry-header">
                      <div className="xlm-revision-history__heading">
                        <h3>Revision {revision.revision}</h3>
                        {current && (
                          <span className="xlm-revision-history__current">
                            Current
                          </span>
                        )}
                        {revision.kind !== "edit" && (
                          <span className="xlm-revision-history__kind">
                            {revisionKindLabel(revision.kind)}
                          </span>
                        )}
                      </div>
                      {!current && (
                        <IconButton
                          tone="quiet"
                          size="xs"
                          label={`Restore revision ${revision.revision}`}
                          tooltip={`Restore revision ${revision.revision}.`}
                          icon={<RestoreIcon />}
                          onClick={() => onRequestRestore(revision)}
                        />
                      )}
                    </header>
                    <p className="xlm-revision-history__edit" title={edit}>
                      <span>{edit}</span>
                    </p>
                    <footer className="xlm-revision-history__meta">
                      <span
                        className="xlm-revision-history__author"
                        title={`Edited by ${revision.created_by ?? "System"}`}
                      >
                        <span className="sr-only">Edited by </span>
                        {author}
                      </span>
                      <span aria-hidden="true">·</span>
                      <time
                        className="xlm-revision-history__time"
                        dateTime={revision.created_at}
                        title={revisionTimestampTitle(revision.created_at)}
                      >
                        <span className="sr-only">Time </span>
                        {relativeTime(revision.created_at)}
                      </time>
                    </footer>
                  </article>
                </li>
              );
            })}
          </ol>
        ) : null}
      </div>
    </SidePeek>
  );
}
